const FALLBACK_TINT = '#ebebf2';
const MAX_SAMPLE_WIDTH = 160;
const SAFETY_TIMEOUT_MS = 4000;
const INK_LUMINANCE_MAX = 220;
const DARK_INK = {
  ink: '#ffffff',
  muted: 'rgba(255, 255, 255, 0.72)',
};
const LIGHT_INK = {
  ink: '#6e6e8d',
  muted: 'rgba(110, 110, 141, 0.72)',
};

/**
 * @param {number} value
 * @returns {string}
 */
const toHex = (value) => value.toString(16).padStart(2, '0');

/**
 * @param {number} red
 * @param {number} green
 * @param {number} blue
 * @returns {string}
 */
const rgbToHex = (red, green, blue) => `#${toHex(red)}${toHex(green)}${toHex(blue)}`;

/**
 * @param {string} src
 * @returns {string}
 */
const withCacheBust = (src) => {
  const join = src.includes('?') ? '&' : '?';
  return `${src}${join}card_tint=1`;
};

/**
 * Average edge tint of a product image for card background.
 * Samples a downscaled copy to avoid memory/canvas failures on large assets.
 * @param {CanvasImageSource} image
 * @param {number} [naturalWidth]
 * @param {number} [naturalHeight]
 * @param {'top' | 'left'} [edge]
 * @returns {string}
 */
const extractCardTint = (image, naturalWidth = 0, naturalHeight = 0, edge = 'top') => {
  const fullWidth = naturalWidth || /** @type {HTMLImageElement} */ (image).naturalWidth || /** @type {HTMLImageElement} */ (image).width;
  const fullHeight = naturalHeight || /** @type {HTMLImageElement} */ (image).naturalHeight || /** @type {HTMLImageElement} */ (image).height;

  if (!fullWidth || !fullHeight) {
    return FALLBACK_TINT;
  }

  const scale = Math.min(1, MAX_SAMPLE_WIDTH / fullWidth);
  const sampleWidth = Math.max(1, Math.round(fullWidth * scale));
  const sampleHeight = Math.max(1, Math.round(fullHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return FALLBACK_TINT;
  }

  try {
    context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
  } catch {
    return FALLBACK_TINT;
  }

  const inset = Math.max(1, Math.round(2 * scale) || 1);
  let sampleX = 0;
  let sampleY = 0;
  let sampleBandWidth = sampleWidth;
  let sampleBandHeight = 8;

  try {
    if (edge === 'left') {
      const searchWidth = Math.max(8, Math.round(sampleWidth * 0.35));
      const searchData = context.getImageData(0, 0, searchWidth, sampleHeight).data;
      const colStride = searchWidth * 4;
      let edgeCol = -1;

      for (let column = 0; column < searchWidth; column += 1) {
        for (let row = 0; row < sampleHeight; row += 1) {
          const index = row * colStride + column * 4;
          if (searchData[index + 3] >= 200) {
            edgeCol = column;
            break;
          }
        }
        if (edgeCol !== -1) {
          break;
        }
      }

      if (edgeCol === -1) {
        return FALLBACK_TINT;
      }

      sampleX = Math.min(edgeCol + inset, sampleWidth - 1);
      sampleBandWidth = Math.min(8, sampleWidth - sampleX);
      sampleBandHeight = sampleHeight;
    } else {
      const searchHeight = Math.max(8, Math.round(sampleHeight * 0.35));
      const searchData = context.getImageData(0, 0, sampleWidth, searchHeight).data;
      const rowStride = sampleWidth * 4;
      let edgeRow = -1;

      for (let row = 0; row < searchHeight; row += 1) {
        for (let column = 0; column < sampleWidth; column += 1) {
          const index = row * rowStride + column * 4;
          if (searchData[index + 3] >= 200) {
            edgeRow = row;
            break;
          }
        }
        if (edgeRow !== -1) {
          break;
        }
      }

      if (edgeRow === -1) {
        return FALLBACK_TINT;
      }

      sampleY = Math.min(edgeRow + inset, sampleHeight - 1);
      sampleBandHeight = Math.min(8, sampleHeight - sampleY);
    }
  } catch {
    return FALLBACK_TINT;
  }

  let data;
  try {
    data = context.getImageData(sampleX, sampleY, sampleBandWidth, sampleBandHeight).data;
  } catch {
    return FALLBACK_TINT;
  }
  const pixelInterval = 4;

  let red = 0;
  let green = 0;
  let blue = 0;
  let count = 0;

  for (let index = 0; index < data.length; index += pixelInterval * 4) {
    if (data[index + 3] < 200) {
      continue;
    }

    red += data[index];
    green += data[index + 1];
    blue += data[index + 2];
    count += 1;
  }

  if (!count) {
    return FALLBACK_TINT;
  }

  return rgbToHex(
    Math.round(red / count),
    Math.round(green / count),
    Math.round(blue / count),
  );
};

/**
 * @param {string} hex
 * @returns {{ ink: string, muted: string }}
 */
const getCardInk = (hex) => {
  const channels = hex
    .slice(1)
    .match(/.{2}/g)
    ?.map((channel) => Number.parseInt(channel, 16));

  if (!channels || channels.length < 3) {
    return LIGHT_INK;
  }

  const luminance = (channels[0] * 299 + channels[1] * 587 + channels[2] * 114) / 1000;
  return luminance < INK_LUMINANCE_MAX ? DARK_INK : LIGHT_INK;
};

/**
 * @param {HTMLElement} card
 * @param {string} cardTopColor
 */
const setCardPalette = (card, cardTopColor) => {
  const cardTopRgb = cardTopColor
    .slice(1)
    .match(/.{2}/g)
    ?.map((channel) => Number.parseInt(channel, 16))
    .join(', ');

  if (!cardTopRgb) {
    return;
  }

  const cardInk = getCardInk(cardTopColor);

  card.style.setProperty('--card-top', cardTopColor);
  card.style.setProperty('--card-top-rgb', cardTopRgb);
  card.style.setProperty('--card-ink', cardInk.ink);
  card.style.setProperty('--card-ink-muted', cardInk.muted);
  card.style.backgroundColor = cardTopColor;
};

/**
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
const loadCorsImage = (src) => new Promise((resolve, reject) => {
  const probe = new Image();
  probe.decoding = 'async';
  probe.crossOrigin = 'anonymous';

  probe.addEventListener('load', () => {
    if (!probe.naturalWidth) {
      reject(new Error('empty image'));
      return;
    }
    resolve(probe);
  }, { once: true });
  probe.addEventListener('error', () => reject(new Error('image error')), { once: true });
  probe.src = src;
});

/**
 * Sample via display image, then CORS clone / cache-bust / fetch blob.
 * @param {HTMLImageElement} image
 * @param {(tint: string) => void} onDone
 * @param {'top' | 'left'} [edge]
 */
const sampleTintFromSrc = (image, onDone, edge = 'top', extraFetch = true) => {
  const src = image.currentSrc || image.src;
  if (!src) {
    onDone(FALLBACK_TINT);
    return;
  }

  const finish = (tint) => {
    onDone(tint || FALLBACK_TINT);
  };

  // Fast path: same-origin decoded image already usable.
  if (image.complete && image.naturalWidth > 0) {
    const direct = extractCardTint(image, 0, 0, edge);
    if (direct !== FALLBACK_TINT) {
      finish(direct);
      return;
    }
  }

  // Catalog listing: never re-download the photo (cache-bust / no-store fights <img>).
  if (!extraFetch) {
    finish(FALLBACK_TINT);
    return;
  }

  (async () => {
    try {
      const probe = await loadCorsImage(src);
      const tint = extractCardTint(probe, 0, 0, edge);
      if (tint !== FALLBACK_TINT) {
        finish(tint);
        return;
      }
    } catch {
      // continue
    }

    // Image may be cached without ACAO — force a CORS revalidate.
    try {
      const probe = await loadCorsImage(withCacheBust(src));
      finish(extractCardTint(probe, 0, 0, edge));
      return;
    } catch {
      // continue
    }

    try {
      const response = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
      if (!response.ok) {
        throw new Error('fetch failed');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const probe = await loadCorsImage(objectUrl);
        finish(extractCardTint(probe, 0, 0, edge));
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      finish(FALLBACK_TINT);
    }
  })();
};

/**
 * @param {HTMLElement} card
 * @returns {boolean}
 */
const isSoftPalette = (card) => card.dataset.cardPaletteSoft === '1';

/**
 * @param {HTMLElement} card
 * @param {HTMLImageElement} image
 * @param {{ force?: boolean, edge?: 'top' | 'left', extraFetch?: boolean }} [options]
 */
const applyPalette = (card, image, options = {}) => {
  const force = Boolean(options.force) || isSoftPalette(card);
  const edge = options.edge === 'left' ? 'left' : 'top';
  const extraFetch = options.extraFetch !== false;
  const alreadyRevealed = card.dataset.cardPaletteInited === '1';

  if (!force && (card.dataset.cardPalettePending === '1' || alreadyRevealed)) {
    return;
  }

  // SKU swap clears inited before re-init so the skeleton can cover the new photo.
  // A late load after the safety timeout must not flash the skeleton again.
  if (force && !alreadyRevealed) {
    delete card.dataset.cardPaletteInited;
  }

  card.dataset.cardPalettePending = '1';
  if (edge === 'left') {
    card.dataset.cardPaletteEdge = 'left';
  }

  sampleTintFromSrc(image, (tint) => {
    // A newer run may have started (force refresh) — ignore stale callbacks.
    if (card.dataset.cardPalettePending !== '1' && card.dataset.cardPaletteInited === '1' && !isSoftPalette(card)) {
      return;
    }

    setCardPalette(card, tint);
    card.dataset.cardPaletteInited = '1';
    delete card.dataset.cardPalettePending;
    delete card.dataset.cardPaletteSoft;
  }, edge, extraFetch);
};

/**
 * WebKit often never starts `loading="lazy"` for covered / off-screen cards.
 * Flip to eager when the card is near the viewport — do not touch src/srcset
 * (Safari can abort the request and swallow the only `load` event).
 *
 * @param {HTMLElement} card
 * @param {HTMLImageElement} image
 */
const nudgeLazyImage = (card, image) => {
  if (image.loading !== 'lazy') {
    return;
  }

  const promote = () => {
    if (image.loading === 'lazy') {
      image.loading = 'eager';
    }
  };

  const rect = card.getBoundingClientRect();
  const margin = 400;
  if (rect.bottom > -margin && rect.top < window.innerHeight + margin) {
    promote();
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    promote();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }
      promote();
      observer.disconnect();
    },
    { rootMargin: '400px 0px' },
  );
  observer.observe(card);
};

/**
 * Fade in when the lazy image is painted (swiper slides, ajax inject).
 * Opacity 0 only via .is-reveal-pending after nudgeLazyImage — not in CSS by default,
 * so WebKit still starts loading="lazy" before JS runs.
 *
 * @param {HTMLImageElement} image
 */
const bindImageReveal = (image) => {
  const reveal = () => {
    image.dataset.imageLoaded = '1';
    image.classList.remove('is-reveal-pending');
  };

  if (image.dataset.imageLoaded === '1') {
    return;
  }

  if (image.complete && image.naturalWidth > 0) {
    reveal();
    return;
  }

  image.classList.add('is-reveal-pending');

  image.addEventListener('load', reveal, { once: true });
  image.addEventListener('error', reveal, { once: true });

  if (image.complete && image.naturalWidth > 0) {
    reveal();
  }
};

/**
 * @param {HTMLElement} card
 * @param {HTMLImageElement} image
 * @param {{ edge?: 'top' | 'left', extraFetch?: boolean }} [options]
 */
const bindImageAndApply = (card, image, options = {}) => {
  const edge = options.edge === 'left' ? 'left' : 'top';
  const extraFetch = options.extraFetch !== false;
  const run = (force = false) => applyPalette(card, image, { force, edge, extraFetch });

  nudgeLazyImage(card, image);
  bindImageReveal(image);

  if (image.complete && image.naturalWidth > 0) {
    run();
    return;
  }

  image.addEventListener('load', () => run(true), { once: true });
  image.addEventListener(
    'error',
    () => {
      setCardPalette(card, FALLBACK_TINT);
      card.dataset.cardPaletteInited = '1';
      delete card.dataset.cardPalettePending;
      delete card.dataset.cardPaletteSoft;
    },
    { once: true },
  );

  // load may have completed between the complete-check and listener attach.
  if (image.complete && image.naturalWidth > 0) {
    run(true);
  }
};

/**
 * Apply image-derived palette CSS vars to product cards.
 * @param {ParentNode} [root=document]
 * @returns {HTMLElement[]}
 */
export function initProductCards(root = document) {
  root.querySelectorAll('.product-card__image').forEach((image) => {
    if (!(image instanceof HTMLImageElement)) {
      return;
    }

    if (image.dataset.imageLoaded === '1') {
      return;
    }

    const card = image.closest('.product-card');
    if (card instanceof HTMLElement) {
      nudgeLazyImage(card, image);
    }
    bindImageReveal(image);
  });

  const cards = [];
  const elements = root.querySelectorAll(
    [
      '.product-card:not([data-card-palette-inited="1"]):not([data-card-palette-pending="1"])',
      // Soft timeout fallback — re-sample when init runs again (ajax / load).
      '.product-card[data-card-palette-soft="1"]',
    ].join(', '),
  );

  elements.forEach((el) => {
    if (!(el instanceof HTMLElement)) {
      return;
    }

    // Soft fallback from a previous safety timeout — allow a real sample now.
    if (isSoftPalette(el)) {
      delete el.dataset.cardPaletteInited;
      delete el.dataset.cardPalettePending;
    }

    const image = el.querySelector('.product-card__image');
    if (!(image instanceof HTMLImageElement)) {
      el.dataset.cardPaletteInited = '1';
      delete el.dataset.cardPaletteSoft;
      return;
    }

    if (el.classList.contains('product-card--wide') || el.classList.contains('card--wide')) {
      if (!el.closest('[data-products-swiper]')) {
        el.style.gridColumn = '1 / -1';
      }
      el.style.width = '100%';
    }

    bindImageAndApply(el, image, { extraFetch: false });
    cards.push(el);
  });

  // Never leave a card on the skeleton: late image load re-samples without flashing.
  window.setTimeout(() => {
    root.querySelectorAll('.product-card:not([data-card-palette-inited="1"])').forEach((el) => {
      if (!(el instanceof HTMLElement)) {
        return;
      }

      if (!el.style.getPropertyValue('--card-top')) {
        setCardPalette(el, FALLBACK_TINT);
      }

      el.dataset.cardPaletteInited = '1';
      el.dataset.cardPaletteSoft = '1';
      delete el.dataset.cardPalettePending;
    });
  }, SAFETY_TIMEOUT_MS);

  return cards;
}

/**
 * Sample a display image and paint --card-top on any host (product card or tile).
 *
 * @param {HTMLElement} card
 * @param {HTMLImageElement} image
 * @param {{ edge?: 'top' | 'left' }} [options]
 */
export function applyCardImagePalette(card, image, options = {}) {
  bindImageAndApply(card, image, options);
}
